import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { HiMenu } from 'react-icons/hi';
import { RxCross1 } from 'react-icons/rx';
import { FiSun, FiMoon, FiGlobe, FiCheck, FiChevronDown } from 'react-icons/fi';

import logo from '../../../imgs/logo-tile.png';
import { Button } from '../button/Button';
import { useSettings } from '../../../context/SettingsContext';
import { Lang } from '../../../i18n/translations';

import './HeaderNav.scss';

interface HeaderNavProps {
  /** Kept for backwards compatibility; active link is derived from the route. */
  currentPageName?: string;
}

const languages: { code: Lang; label: string }[] = [
  { code: 'nl', label: 'Nederlands' },
  { code: 'en', label: 'English' },
];

const LanguagePicker: React.FC = () => {
  const { lang, setLang, t } = useSettings();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className="ds-lang" ref={ref}>
      <button
        className="ds-lang__btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t.nav.language}
        onClick={() => setOpen((v) => !v)}
      >
        <FiGlobe aria-hidden="true" />
        <span>{lang.toUpperCase()}</span>
        <FiChevronDown className={`ds-lang__chevron ${open ? 'is-open' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <ul className="ds-lang__menu" role="listbox" aria-label={t.nav.language}>
          {languages.map((l) => (
            <li key={l.code}>
              <button
                role="option"
                aria-selected={lang === l.code}
                className={`ds-lang__option ${lang === l.code ? 'is-active' : ''}`}
                onClick={() => {
                  setLang(l.code);
                  setOpen(false);
                }}
              >
                {l.label}
                {lang === l.code && <FiCheck aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const HeaderNav: React.FC<HeaderNavProps> = () => {
  const { t, theme, toggleTheme } = useSettings();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const links = [
    { label: t.nav.home, to: '/' },
    { label: t.nav.services, to: '/services' },
    { label: t.nav.work, to: '/work' },
    { label: t.nav.about, to: '/about' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <header className={`ds-nav ${scrolled ? 'ds-nav--scrolled' : ''}`}>
      <nav className="ds-nav__inner ds-container" aria-label="Primary">
        <Link to="/" className="ds-nav__brand" onClick={() => setOpen(false)}>
          <img src={logo} alt="DS-Innovation" width={40} height={40} />
          <span>DS-Innovation</span>
        </Link>

        <ul className={`ds-nav__links ${open ? 'is-open' : ''}`}>
          {links.map((link) => (
            <li key={link.to}>
              <NavLink
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) => `ds-nav__link ${isActive ? 'is-active' : ''}`}
                onClick={() => setOpen(false)}
              >
                {link.label}
              </NavLink>
            </li>
          ))}
          <li className="ds-nav__cta-mobile">
            <Button to="/contact" onClick={() => setOpen(false)}>
              {t.nav.cta}
            </Button>
          </li>
        </ul>

        <div className="ds-nav__actions">
          <button
            className="ds-nav__icon-btn"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? t.nav.toLight : t.nav.toDark}
            title={theme === 'dark' ? t.nav.toLight : t.nav.toDark}
          >
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>

          <LanguagePicker />

          <div className="ds-nav__cta-desktop">
            <Button to="/contact">{t.nav.cta}</Button>
          </div>

          <button
            className="ds-nav__toggle"
            aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <RxCross1 /> : <HiMenu />}
          </button>
        </div>
      </nav>
    </header>
  );
};

export default HeaderNav;
